import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  addIcon,
  backArrowIcon,
  filterIcon,
  refreshIcon,
  searchIcon,
} from "../../assets/icons/SvgIconList";
import ICButton from "./ICButton";
import { ICCheckbox, ICTextInput } from "../form";
import {
  dateFormat,
  prepareMessageFromParams,
  timeFormat,
} from "../../utils/AppFunctions";

interface ICCMMMassage {
  name: string;
  date: Date;
}
interface IAdvanceSearch {
  handleOnClickAdd?: () => void;
  handleSearchChange?: (text: string) => void;
  searchText?: string;
  handleDeleteCheckBox?: (check: boolean) => void;
  filterCheckBox?: boolean;
  filterNode?: React.ReactNode;
  defaultOpen?: boolean;
  refreshTable?: () => void;
  title?: string;
  backButton?: boolean;
  createMassage?: ICCMMMassage;
  modifiedMassage?: ICCMMMassage;
}

const AdvanceSearch = ({
  handleOnClickAdd,
  handleSearchChange,
  searchText,
  handleDeleteCheckBox,
  filterCheckBox,
  filterNode,
  defaultOpen = false,
  refreshTable,
  title,
  backButton,
  createMassage,
  modifiedMassage,
}: IAdvanceSearch) => {
  const { t } = UseTranslationHook();
  const location = useLocation();
  const navigate = useNavigate();
  const [showFilter, setShowFilter] = useState(defaultOpen);

  const slugHeading = location.pathname.replace("/admin/", "");
  let normalString = slugHeading.replace(/-/g, " ");
  let heading = normalString.replace(/\b\w/g, function (char) {
    return char.toUpperCase();
  });

  const formatMessage = (
    type: "create" | "modified",
    massageData: ICCMMMassage
  ) => {
    const messageTemplate =
      type === "create" ? t("form_create_info") : t("form_modified_info");
    return prepareMessageFromParams(messageTemplate, [
      ["createName", massageData.name],
      ["createDate", dateFormat(massageData.date)],
      ["createTime", timeFormat(massageData.date)],
    ]);
  };

  return (
    <div className="bg-subtle rounded-md p-4">
      <div className="min-w-max w-full flex items-center">
        {backButton && (
          <div className="mr-4">
            <ICButton
              className="size-9 text-accent grid place-content-center bg-accent-muted rounded-md cursor-pointer"
              onClick={() => {
                navigate(-1);
              }}
            >
              <div className="size-7"> {backArrowIcon}</div>
            </ICButton>
          </div>
        )}
        <h4 className="me-auto flex items-center">{title ?? heading}</h4>
        <div className="items-center">
          {createMassage && (
            <div className="font-light text-disable">
              {formatMessage("create", createMassage)}
            </div>
          )}
          {modifiedMassage && (
            <div className="font-light text-disable">
              {formatMessage("modified", modifiedMassage)}
            </div>
          )}
        </div>
        {handleDeleteCheckBox && (
          <ICCheckbox
            label={t("show_deleted")}
            checked={filterCheckBox ?? false}
            onChange={(e) => {
              handleDeleteCheckBox(e);
            }}
          />
        )}
        {refreshTable && (
          <div className="!py-0 ml-4">
            <ICButton
              className="size-9 text-accent grid place-content-center bg-accent-muted rounded-md cursor-pointer"
              onClick={refreshTable}
            >
              <div className="size-7"> {refreshIcon}</div>
            </ICButton>
          </div>
        )}

        {filterNode && (
          <div className="!py-0 ml-4">
            <ICButton
              type="button"
              onClick={() => setShowFilter((prev) => !prev)}
              className="flex items-center h-9"
            >
              <div className="h-5 w-5">{filterIcon}</div>
              &nbsp;{t("filter")}
            </ICButton>
          </div>
        )}
        {handleSearchChange && (
          <div className="relative ml-4">
            <ICTextInput
              containerClassName={"!h-9 mr-2"}
              leading={<div className="size-5 mr-1">{searchIcon}</div>}
              placeholder={t("search")}
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchText}
            />
          </div>
        )}

        {handleOnClickAdd && (
          <div className="!py-0">
            <ICButton
              type="button"
              onClick={handleOnClickAdd}
              className="ml-4 flex items-center h-9 !w-fit"
            >
              <div className="h-5 w-5">{addIcon}</div>
              &nbsp;{t("add_data")}
            </ICButton>
          </div>
        )}
      </div>
      {showFilter && filterNode && (
        <div className="w-full pt-4">{filterNode}</div>
      )}
    </div>
  );
};

export default AdvanceSearch;
